/**
 * ---------------------------------------------------------------------------
 * PRODUCT GALLERY - MOBILE AUTOSCROLL + VIDEO PLAYBACK GUARD
 * ---------------------------------------------------------------------------
 *
 * Two independent controllers on the theme's <media-carousel>:
 *
 * 1. GalleryAutoscroll advances the gallery on its own on mobile, then hands
 *    control over to the visitor for good as soon as they touch it.
 *
 * 2. GalleryVideoKeeper makes the merchant's "video autoplay" setting actually
 *    hold. The theme pauses every video and replays the current one on each
 *    `carousel:settle`, but `BaseMedia.play()` is guarded by a `playing`
 *    attribute that DOM events only update asynchronously. So on a second
 *    settle the pause goes through while the replay is silently skipped, and
 *    the video dies. A second settle is routine on a video slide: the carousel
 *    is `adaptive-height`, the slide's aspect ratio differs from the images
 *    around it, and both the height write and the poster/video swap trigger a
 *    reflow that the `scrollend` polyfill turns into another settle 75ms later.
 *    Rather than replaying afterwards, which is visible as a stutter, the
 *    keeper stops the pause from reaching the video in the first place.
 *
 * Both use the carousel's public API only. Nothing in theme.js / theme.css is
 * patched, so a theme update cannot silently undo any of this.
 *
 * Configured from the markup:
 *   data-autoscroll                enables the rotation
 *   data-autoscroll-delay="4000"   dwell time per media, in ms
 *   data-autoscroll-mode           "loop" (default) | "once"
 *   data-autoscroll-progress       "true" to fill the active page dot
 *
 * The playback guard needs no configuration: it follows the carousel's own
 * `autoplay` attribute, i.e. the "Enable video autoplay" section setting.
 */

(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  // Settling time before re-checking after a pause we did not sanction.
  var PLAYBACK_CHECK_DELAY = 120;

  // A pause landing this soon after a tap on the video is the visitor using
  // the native controls, and is left alone.
  var USER_INTENT_WINDOW = 700;

  // Fallback delay in case `scrollend` never lands (older Safari, interrupted
  // scroll), so a missed settle event can never freeze the rotation.
  var SETTLE_WATCHDOG = 1600;

  // A video holds its slide for its own duration, never longer.
  var VIDEO_MAX_DWELL = 15000;

  // Small pause left after a clip ends, before moving on.
  var VIDEO_TAIL = 400;

  // Movement, in px, above which a gesture is an intent rather than a jitter.
  var GESTURE_THRESHOLD = 10;

  var RUNNING_CLASS = 'pg-autoscrolling';
  var DWELL_PROPERTY = '--pg-autoscroll-dwell';

  var booted = new WeakSet();

  var GalleryAutoscroll = class {
    constructor(carousel) {
      this._carousel = carousel;
      this._gallery = carousel.closest('product-gallery') || carousel;

      this._delay = Math.max(1000, parseInt(carousel.dataset.autoscrollDelay, 10) || 4000);
      this._rewind = carousel.dataset.autoscrollMode !== 'once';
      this._showProgress = carousel.dataset.autoscrollProgress === 'true';

      this._abortController = new AbortController();
      this._timer = null;
      this._watchdog = null;
      this._pointer = null;
      this._inView = false;
      this._allowed = false;
      this._released = false;

      var signal = this._abortController.signal;

      // Hand over control on a deliberate gesture. We deliberately do not
      // listen to `scroll`: our own programmatic scrolls emit it too, which
      // would stop the rotation on the very first move.
      this._gallery.addEventListener('pointerdown', this._onPointerStart, {
        passive: true,
        capture: true,
        signal
      });
      ['pointerup', 'pointercancel'].forEach((type) => {
        this._gallery.addEventListener(type, this._onPointerEnd, { passive: true, capture: true, signal });
      });
      this._gallery.addEventListener('wheel', this._onWheel, { passive: true, capture: true, signal });
      this._gallery.addEventListener('keydown', this._release, { signal });

      if (window.Shopify && Shopify.designMode) {
        this._gallery.addEventListener('shopify:block:select', this._release, { signal });
      }

      // The dwell starts once the media has actually settled, not while it is
      // still sliding in, so every image is on screen for the same time.
      this._carousel.addEventListener('carousel:settle', this._schedule, { signal });

      // Negative margins keep the rotation tied to the middle of the viewport,
      // which also works for galleries taller than the screen (a ratio-based
      // threshold could never be reached there).
      this._observer = new IntersectionObserver(
        (entries) => {
          this._inView = entries[entries.length - 1].isIntersecting;
          this._inView ? this._schedule() : this._pause();
        },
        { rootMargin: '-15% 0px -15% 0px' }
      );
      this._observer.observe(this._carousel);

      document.addEventListener('visibilitychange', () => (document.hidden ? this._pause() : this._schedule()), {
        signal
      });

      this._mobileQuery = window.matchMedia(MOBILE_QUERY);
      this._motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      [this._mobileQuery, this._motionQuery].forEach((query) => {
        query.addEventListener('change', () => this._evaluate(), { signal });
      });

      this._evaluate();
    }

    get slides() {
      return this._carousel.visibleItems;
    }

    get canRun() {
      return (
        !this._released &&
        this._allowed &&
        this._inView &&
        !this._pointer && // never advance under a finger that is still down
        !document.hidden &&
        this.slides.length > 1
      );
    }

    /**
     * Mobile only, and only when motion is welcome. Re-evaluated on rotation
     * and on system preference changes.
     */
    _evaluate() {
      this._allowed = this._mobileQuery.matches && !this._motionQuery.matches;
      this._allowed ? this._schedule() : this._pause();
    }

    _schedule = () => {
      this._clearTimers();

      if (!this.canRun) {
        this._setRunning(false);
        return;
      }

      var dwell = this._dwell();
      this._setRunning(true, dwell);
      this._timer = setTimeout(this._advance, dwell);
    };

    _pause() {
      this._clearTimers();
      this._setRunning(false);
    }

    /**
     * The rotation is held for as long as a finger is down, whatever the
     * gesture turns out to be.
     */
    _onPointerStart = (event) => {
      this._pointer = { x: event.clientX, y: event.clientY };
      this._pause();
    };

    /**
     * Tells apart the three gestures that start on the gallery:
     *
     *  - a horizontal swipe, or a tap on the media / a page dot / a thumbnail
     *    / the zoom button -> the visitor is browsing the gallery, hand over;
     *  - a vertical page scroll that merely started over the image -> keep
     *    rotating, this is not about the gallery at all;
     *  - anything ambiguous (a cancelled gesture, a pinch) -> keep rotating,
     *    the safe default.
     */
    _onPointerEnd = (event) => {
      var start = this._pointer;

      if (!start) {
        return;
      }

      this._pointer = null;

      var dx = Math.abs(event.clientX - start.x);
      var dy = Math.abs(event.clientY - start.y);

      if ((dx > GESTURE_THRESHOLD && dx >= dy) || (event.type === 'pointerup' && dx <= GESTURE_THRESHOLD && dy <= GESTURE_THRESHOLD)) {
        this._release();
        return;
      }

      this._schedule();
    };

    /**
     * Trackpads and mice: only a horizontal intent counts as browsing the
     * gallery, a vertical page scroll does not.
     */
    _onWheel = (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        this._release();
      }
    };

    /**
     * The visitor is now in charge: tear everything down, definitively.
     */
    _release = () => {
      if (this._released) {
        return;
      }

      this._released = true;
      this._pause();
      this._observer.disconnect();
      this._abortController.abort();
      this._gallery.style.removeProperty(DWELL_PROPERTY);
    };

    _advance = () => {
      var slides = this.slides;

      if (slides.length < 2) {
        this._pause();
        return;
      }

      var current = slides.indexOf(this._carousel.selectedSlide);
      var isLast = current >= slides.length - 1;

      if (isLast && !this._rewind) {
        this._release();
        return;
      }

      var target = slides[isLast ? 0 : current + 1];

      this._setRunning(false);

      // Smooth-scrolling back across the whole strip reads as a blur, so the
      // loop back to the first media is a straight cut.
      this._carousel.select(this._carousel.items.indexOf(target), { animate: !isLast });
      this._watchdog = setTimeout(this._schedule, SETTLE_WATCHDOG);
    };

    /**
     * A video slide is held for what is left of the clip instead of the
     * configured delay, so the visitor is never scrolled away mid-play. The
     * theme starts playback asynchronously on arrival, so we must not require
     * the video to be playing already at this point.
     */
    _dwell() {
      var slide = this._carousel.selectedSlide;

      if (!slide || slide.getAttribute('data-media-type') === 'image') {
        return this._delay;
      }

      var video = slide.querySelector('video');

      if (video && video.duration) {
        var remaining = (video.duration - video.currentTime) * 1000 + VIDEO_TAIL;
        return Math.min(Math.max(remaining, this._delay), VIDEO_MAX_DWELL);
      }

      if (video) {
        // Metadata is not in yet: re-measure as soon as the duration is known.
        video.addEventListener('loadedmetadata', this._schedule, {
          once: true,
          signal: this._abortController.signal
        });
      }

      // Embedded players (YouTube, Vimeo) and 3D models expose no duration,
      // so they simply get a longer turn than a still image.
      return Math.min(this._delay * 2, VIDEO_MAX_DWELL);
    }

    /**
     * Drives the progress fill on the active page dot. The class is removed
     * and re-added around a forced reflow so the CSS animation restarts from
     * zero even when the same dot stays current (once every few seconds, the
     * cost is irrelevant).
     */
    _setRunning(state, dwell) {
      if (!this._showProgress) {
        return;
      }

      this._gallery.classList.remove(RUNNING_CLASS);

      if (!state) {
        return;
      }

      void this._gallery.offsetWidth;
      this._gallery.style.setProperty(DWELL_PROPERTY, dwell + 'ms');
      this._gallery.classList.add(RUNNING_CLASS);
    }

    _clearTimers() {
      clearTimeout(this._timer);
      clearTimeout(this._watchdog);
      this._timer = this._watchdog = null;
    }
  };

  /**
   * Makes the merchant's "video autoplay" setting actually hold, without
   * patching theme.js. Independent of GalleryAutoscroll: it must survive the
   * visitor taking control of the gallery, and it runs on every viewport.
   *
   * Two moves, in order of importance:
   *
   * 1. Scripted pauses aimed at the video of the CURRENT slide are swallowed.
   *    Racing to undo them cannot be smooth, because the video is visibly
   *    stopped in the meantime. Native controls pause the element internally
   *    rather than through this method, so the visitor keeps full control.
   *
   * 2. Playback starts on `carousel:change`, when the slide is picked, instead
   *    of on `carousel:settle`, once the scroll is over. Together with warming
   *    up the neighbouring clip, the video is already running when the slide
   *    lands rather than starting to buffer at that point.
   */
  var GalleryVideoKeeper = class {
    constructor(carousel) {
      this._carousel = carousel;
      this._gallery = carousel.closest('product-gallery') || carousel;
      this._abortController = new AbortController();
      this._timer = null;
      this._lastPointerAt = 0;

      var signal = this._abortController.signal;

      // `play` and `pause` do not bubble, so we catch them on the way down.
      this._gallery.addEventListener('pause', this._onVideoPause, { capture: true, signal });
      this._gallery.addEventListener('play', this._onVideoPlay, { capture: true, signal });

      ['pointerdown', 'pointerup'].forEach((type) => {
        this._gallery.addEventListener(type, this._onPointer, { capture: true, passive: true, signal });
      });

      this._carousel.addEventListener('carousel:change', this._sync, { signal });
      this._carousel.addEventListener('carousel:settle', this._sync, { signal });
      document.addEventListener('visibilitychange', this._sync, { signal });

      // The theme emits no settle for the media the gallery opens on.
      this._sync();
    }

    get videos() {
      return (this._videos =
        this._videos || Array.from(this._carousel.querySelectorAll('[data-media-type="video"] video')));
    }

    get currentVideo() {
      var slide = this._carousel.selectedSlide;

      // Embedded players (YouTube, Vimeo) expose no <video> element and are
      // driven by the theme through their own API.
      if (!slide || slide.getAttribute('data-media-type') !== 'video') {
        return null;
      }

      return slide.querySelector('video');
    }

    _sync = () => {
      var current = this.currentVideo;

      // Leaving a slide clears its hold, so coming back to it starts over.
      this.videos.forEach((video) => {
        if (video !== current) {
          delete video.dataset.pgHold;
        }
      });

      this._warmNeighbours();

      if (!current) {
        return;
      }

      this._protect(current);

      if (current.paused && !current.ended && !current.dataset.pgHold && !document.hidden) {
        this._start(current);
      }
    };

    /**
     * Swallows the pauses the theme fires at the video currently on screen.
     */
    _protect(video) {
      if (video.pgProtected) {
        return;
      }

      video.pgProtected = true;

      var keeper = this;
      var nativePause = HTMLMediaElement.prototype.pause;

      video.pause = function () {
        if (video === keeper.currentVideo && video.dataset.pgHold !== 'user' && !document.hidden) {
          return;
        }

        return nativePause.call(video);
      };
    }

    _start(video) {
      var host = video.closest('video-media');

      // Until <video-media> carries `loaded`, theme.css keeps the video at
      // `visibility: hidden` behind its poster. Touching the theme's player
      // getter is what sets it, and it also wires the listeners that keep the
      // element's `playing` state honest. WebKit will not start a video that
      // is not actually rendered, so this has to happen before play().
      if (host) {
        void host.player;
      }

      // WebKit is markedly more reliable about allowing an unattended play()
      // when muted is set as a property, not only as an attribute.
      video.muted = true;

      var started = video.play();

      if (started && started.catch) {
        started.catch(function () {
          // Refused by the browser (low power mode, a policy we cannot meet):
          // stop asking, and let the theme's poster and native controls stand.
          video.dataset.pgHold = 'blocked';
        });
      }
    }

    /**
     * The clip one slide away is told to buffer, so arriving on it shows a
     * running video rather than a poster waiting on the network.
     */
    _warmNeighbours() {
      var slides = this._carousel.visibleItems;
      var index = slides.indexOf(this._carousel.selectedSlide);

      [index - 1, index + 1].forEach((position) => {
        var slide = slides[position];

        if (!slide || slide.getAttribute('data-media-type') !== 'video') {
          return;
        }

        var video = slide.querySelector('video');

        if (video && video.preload !== 'auto') {
          video.preload = 'auto';
        }
      });
    }

    _onPointer = (event) => {
      if (event.target.tagName === 'VIDEO') {
        this._lastPointerAt = Date.now();
      }
    };

    _onVideoPause = (event) => {
      var video = event.target;

      if (video.tagName !== 'VIDEO') {
        return;
      }

      if (Date.now() - this._lastPointerAt < USER_INTENT_WINDOW) {
        // The visitor pressed pause on the native controls.
        video.dataset.pgHold = 'user';
        return;
      }

      // A pause that got past the guard (browser power saving, an app script):
      // come back to it once the dust has settled.
      this._queueSync();
    };

    _onVideoPlay = (event) => {
      if (event.target.tagName === 'VIDEO') {
        delete event.target.dataset.pgHold;
      }
    };

    _queueSync = () => {
      clearTimeout(this._timer);
      this._timer = setTimeout(this._sync, PLAYBACK_CHECK_DELAY);
    };
  };

  function init(root) {
    (root || document).querySelectorAll('media-carousel[data-autoscroll], media-carousel[autoplay]').forEach((carousel) => {
      if (booted.has(carousel)) {
        return;
      }

      booted.add(carousel);

      // Wait for the upgrade, otherwise `select()`, `selectedSlide` and
      // `visibleItems` are not there yet (theme.js is loaded as a module).
      customElements.whenDefined('media-carousel').then(() => {
        if (!carousel.isConnected) {
          return;
        }

        if (carousel.hasAttribute('autoplay') && carousel.querySelector('[data-media-type="video"]')) {
          new GalleryVideoKeeper(carousel);
        }

        if (carousel.hasAttribute('data-autoscroll')) {
          new GalleryAutoscroll(carousel);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
